import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../App';

export default function SaftaUpdateView({ setView, Header }) {
  const accent = '#0284c7';
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [activeModalOrder, setActiveModalOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    safta_code: '',
    hsn_code: '',
    coo_no: '',
    issue_dt: '',
    remark: '',
    attachment_file: null,
    attachment_filename: null
  });

  const loadData = () => {
    api('/safta/orders')
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
  };

  useEffect(() => {
    loadData();
  }, []);

  const openModal = order => {
    setActiveModalOrder(order);
    setFormData({
      safta_code: order.safta_code || '',
      hsn_code: order.hsn_code || '6203.42.00',
      coo_no: order.coo_no || '',
      issue_dt: order.issue_dt ? String(order.issue_dt).slice(0, 10) : new Date().toISOString().slice(0, 10),
      remark: order.remark || '',
      attachment_file: null,
      attachment_filename: order.attachment_filename || null
    });
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      alert('Attachment size cannot exceed 8MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      setFormData(prev => ({
        ...prev,
        attachment_file: ev.target.result,
        attachment_filename: file.name
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!activeModalOrder) return;
    setSaving(true);
    try {
      const res = await api('/safta/update', {
        method: 'POST',
        body: JSON.stringify({
          tas_no: activeModalOrder.tas_no,
          status: 'ISSUED',
          ...formData
        })
      });
      if (res && res.success) {
        setActiveModalOrder(null);
        loadData();
      } else {
        alert(res?.error || 'Failed to update SAFTA details');
      }
    } catch (e) {
      alert('Error saving SAFTA details');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter(r => {
      if (q) {
        const query = q.toLowerCase();
        return (r.tas_no && r.tas_no.toLowerCase().includes(query)) ||
               (r.buyer && r.buyer.toLowerCase().includes(query)) ||
               (r.factory && r.factory.toLowerCase().includes(query)) ||
               (r.safta_code && r.safta_code.toLowerCase().includes(query)) ||
               (r.hsn_code && r.hsn_code.toLowerCase().includes(query));
      }
      return true;
    });
  }, [rows, q]);

  const fmtD = d => {
    if (!d) return '—';
    const s = String(d).slice(0, 10).split('-');
    return s.length === 3 ? `${s[2]}-${s[1]}-${s[0].slice(2)}` : d;
  };

  return (
    <div className="screen">
      <Header subtitle="TAS · DAILY ACTIVITY · SAFTA WORKING" />

      {/* Title Bar */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('tas-daily')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 20, color: accent }}>
            🛂 Final SAFTA & HSN Working
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            RW Daily Activity: Enter final SAFTA / HSN / Tariff codes and attach document to clear factory shipment.
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={loadData} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer', fontSize: 12 }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{ padding: '12px 20px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
        <div style={{ background: 'rgba(2, 132, 199, 0.08)', border: '1px solid rgba(2, 132, 199, 0.25)', borderRadius: 8, padding: '8px 14px', flex: 1, minWidth: 280 }}>
          <div style={{ fontWeight: 700, color: accent }}>🔒 Factory Shipment Clearance & Document Vault</div>
          <div style={{ color: 'var(--text2)', marginTop: 2, lineHeight: 1.4 }}>
            Once the final SAFTA / HSN codes are saved and certificate attached, the factory is authorized to generate Ex-Factory shipments.
          </div>
        </div>
      </div>

      {/* Filters Toolbar */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search TAS No, Buyer, Factory, HSN, SAFTA Code..."
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, minWidth: 320 }}
        />
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
          {filtered.length} order{filtered.length !== 1 ? 's' : ''} eligible
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '16px 20px', overflowX: 'auto' }}>
        {!rows && <div style={{ color: 'var(--text3)', padding: 20 }}>Loading eligible orders...</div>}
        {rows && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text3)' }}>
            No TAS orders found with approved Final Costing. Orders will automatically appear here as soon as SBD approves their Final Costing.
          </div>
        )}
        {rows && rows.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
            <thead>
              <tr style={{ background: 'var(--bg3)', borderBottom: '2px solid var(--border)', color: 'var(--text2)' }}>
                <th style={{ padding: '10px 12px' }}>TAS No</th>
                <th style={{ padding: '10px 12px' }}>Buyer</th>
                <th style={{ padding: '10px 12px' }}>Factory</th>
                <th style={{ padding: '10px 12px' }}>Order Pcs</th>
                <th style={{ padding: '10px 12px' }}>Final Unit Cost</th>
                <th style={{ padding: '10px 12px' }}>HSN Code</th>
                <th style={{ padding: '10px 12px' }}>SAFTA Code / Ref</th>
                <th style={{ padding: '10px 12px' }}>Attachment</th>
                <th style={{ padding: '10px 12px' }}>Shipment Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => {
                const isDone = !!(r.safta_code || r.hsn_code || r.coo_no);
                return (
                  <tr key={r.tas_no} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'var(--bg)' : 'var(--bg2)' }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700, color: accent }}>{r.tas_no}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.buyer}</td>
                    <td style={{ padding: '10px 12px' }}>{r.factory}</td>
                    <td style={{ padding: '10px 12px' }}>{Number(r.order_qty || 0).toLocaleString('en-IN')} pcs</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                      ₹{Number(r.f_inr || 0).toFixed(2)} (${Number(r.f_cog || 0).toFixed(2)})
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.hsn_code ? <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text)' }}>{r.hsn_code}</span> : <span style={{ color: 'var(--text3)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.safta_code || r.coo_no ? (
                        <div>
                          <div style={{ fontWeight: 700, color: '#10b981' }}>{r.safta_code || r.coo_no}</div>
                          {r.issue_dt && <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>Dated: {fmtD(r.issue_dt)}</div>}
                        </div>
                      ) : (
                        <span style={{ color: '#ef4444', fontWeight: 600 }}>Pending Working</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.has_attachment ? (
                        <a
                          href={`/api/safta/download/${encodeURIComponent(r.tas_no)}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                            background: 'rgba(2, 132, 199, 0.15)', color: accent, border: '1px solid rgba(2, 132, 199, 0.35)',
                            textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4
                          }}
                        >
                          📎 {r.attachment_filename || 'Download'}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--text3)' }}>No File</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: isDone ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: isDone ? '#10b981' : '#ef4444',
                        border: isDone ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid rgba(239, 68, 68, 0.35)'
                      }}>
                        {isDone ? '✅ Cleared' : '🔒 Blocked'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => openModal(r)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                          background: `${accent}18`, color: accent, border: `1px solid ${accent}50`
                        }}
                      >
                        {isDone ? 'Edit Working' : 'Enter Working'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal for direct final SAFTA working + Attachment */}
      {activeModalOrder && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600, padding: 16
        }}>
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            width: '100%', maxWidth: 460, padding: 20, boxShadow: '0 12px 30px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: accent }}>SAFTA Working · {activeModalOrder.tas_no}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{activeModalOrder.buyer} · {activeModalOrder.factory}</div>
              </div>
              <button onClick={() => setActiveModalOrder(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 12 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>SAFTA Code / COO Registration No</label>
                <input
                  type="text"
                  placeholder="e.g. SAFTA-BD-2026-9042 or COO Ref No"
                  value={formData.safta_code}
                  onChange={e => setFormData({ ...formData, safta_code: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Garment HSN / Tariff Code</label>
                <input
                  type="text"
                  placeholder="e.g. 6203.42.00 (Trousers/Shorts) or 6205.20.00 (Shirts)"
                  value={formData.hsn_code}
                  onChange={e => setFormData({ ...formData, hsn_code: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Working / Issue Date</label>
                <input
                  type="date"
                  value={formData.issue_dt}
                  onChange={e => setFormData({ ...formData, issue_dt: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </div>

              {/* Attachment field */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Attach SAFTA / COO Document (PDF / Image)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      padding: '7px 14px', borderRadius: 6, border: '1px dashed var(--border)',
                      background: 'var(--bg)', color: accent, fontWeight: 600, cursor: 'pointer', fontSize: 12
                    }}
                  >
                    📎 {formData.attachment_filename ? 'Change File' : 'Choose File'}
                  </button>
                  <span style={{ color: formData.attachment_filename ? '#10b981' : 'var(--text3)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formData.attachment_filename || 'No file selected'}
                  </span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Remarks / Verification Note</label>
                <input
                  type="text"
                  placeholder="Optional notes..."
                  value={formData.remark}
                  onChange={e => setFormData({ ...formData, remark: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button
                onClick={() => setActiveModalOrder(null)}
                style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
              >
                {saving ? 'Saving...' : 'Save & Allow Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
